import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Ticket, ShoppingBag, BarChart3, Wallet, Receipt,
  Users, CreditCard, QrCode, User, Bus, Settings, Menu, X, Sun, Moon, LogOut, Building2, BookOpen, Globe, Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

// V2 spec: Staff sees ONLY Commandes / Caisse / Personnel / Code QR / Menu.
// Agency space is strictly for createur/admin/staff — super_admin ne doit jamais y accéder.
// staff:true = visible to staff. roles:[] = admin/createur only.
const NAV = [
  { to: "/app", label: "Menu", icon: LayoutDashboard, end: true, staff: true },
  { to: "/app/orders", label: "Commandes", icon: ShoppingBag, staff: true },
  { to: "/app/cash", label: "Caisse", icon: Wallet, staff: true },
  { to: "/app/staff", label: "Personnel", icon: Users, staff: true },
  { to: "/app/qr", label: "Code QR", icon: QrCode, staff: true },
  // Admin+ only
  { to: "/app/dashboard", label: "Tableau de bord", icon: BarChart3, roles: ["createur", "admin"] },
  { to: "/app/routes", label: "Trajets", icon: Bus, roles: ["createur", "admin"] },
  { to: "/app/customers", label: "Clients", icon: User, roles: ["createur", "admin"] },
  { to: "/app/accounting", label: "Comptabilité", icon: BookOpen, roles: ["createur", "admin"] },
  { to: "/app/payments", label: "Paiements", icon: CreditCard, roles: ["createur", "admin"] },
  { to: "/app/plans", label: "Abonnement", icon: Receipt, roles: ["createur"] },
  { to: "/app/settings", label: "Paramètres", icon: Settings, roles: ["createur", "admin"] },
];

export default function DashboardLayout() {
  const { user, company, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (user?.full_name || user?.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  const visibleNav = NAV.filter((n) => {
    if (user?.role === "staff") return !!n.staff;
    if (n.roles && !n.roles.includes(user?.role)) return false;
    return true;
  });

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex md:w-64 flex-col fixed inset-y-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 z-30">
        <div className="h-16 px-6 flex items-center gap-2 border-b border-slate-200 dark:border-slate-800">
          <img src="https://customer-assets-0z36b82j.emergentagent.net/job_ticketing-transport/artifacts/5p6cedk3_1000607872.png" alt="Transport.pro" className="w-10 h-10 rounded-xl object-cover" />
          <div className="min-w-0">
            <div className="font-display font-bold text-sm truncate">{company?.name || "Transport.pro"}</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">{company?.plan || "FREE"}</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 flex flex-col">
          <div className="space-y-1">
            {visibleNav.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-orange-500 text-white shadow-sm"
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </NavLink>
            ))}
            {company?.id && (
              <a
                href={`/book/${company.id}`}
                target="_blank"
                rel="noreferrer"
                data-testid="nav-public-menu-link"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-500/10 border border-dashed border-orange-300 dark:border-orange-500/40 mt-3"
              >
                <Globe className="w-4 h-4" />
                <span>Menu public client</span>
              </a>
            )}
          </div>
          {user?.role === "super_admin" && (
            <NavLink
              to="/superadmin"
              data-testid="nav-superadmin-link"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200 mt-auto shadow-md ring-1 ring-slate-900/10"
            >
              <Shield className="w-4 h-4" />
              <span>Console Super Admin</span>
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-slate-200 dark:border-slate-800 flex items-center gap-2">
          <Avatar className="w-9 h-9"><AvatarFallback className="bg-orange-100 text-orange-700 text-xs font-bold">{initials}</AvatarFallback></Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{user?.full_name}</div>
            <div className="text-xs text-slate-500 capitalize">{user?.role}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" title="Déconnexion">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)}>
          <div className="w-72 h-full bg-white dark:bg-slate-900 p-3 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex items-center gap-2">
                <img src="https://customer-assets-0z36b82j.emergentagent.net/job_ticketing-transport/artifacts/5p6cedk3_1000607872.png" alt="Transport.pro" className="w-8 h-8 rounded-lg object-cover" />
                <div className="font-display font-bold">{company?.name || "Transport.pro"}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)}><X className="w-4 h-4" /></Button>
            </div>
            {visibleNav.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ${
                    isActive ? "bg-orange-500 text-white" : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`
                }
              >
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 md:pl-64 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b border-slate-200 dark:border-slate-800 glass sticky top-0 z-20 flex items-center px-4 md:px-6 gap-3">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(true)} data-testid="open-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="hidden md:flex items-center gap-2 text-sm text-slate-500">
            <Building2 className="w-4 h-4" />
            <span>{company?.name}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle-btn"
              title={theme === "dark" ? "Mode clair" : "Mode sombre"}
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <NavLink to="/app/settings" className="hidden sm:block" data-testid="header-settings-link">
              <Button variant="ghost" size="icon"><Settings className="w-4 h-4" /></Button>
            </NavLink>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="header-logout-btn" className="md:hidden"><LogOut className="w-4 h-4" /></Button>
          </div>
        </header>
        <main className="flex-1 relative z-10 p-4 md:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
